import { type TextareaHTMLAttributes, useId } from 'react';
import styles from './Textarea.module.css';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

/**
 * Multi-line text input. Mirrors the label/hint/error/aria-describedby
 * pattern of the `TextInput` primitive so forms can mix the two without
 * a visual seam. Vertically resizable; default size is set via the `rows`
 * prop at the call site (3–5 is the usual range for short context fields).
 */
export function Textarea({
  label,
  hint,
  error,
  className,
  id: idProp,
  rows = 4,
  ...rest
}: TextareaProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const textareaCls = [styles.textarea, error ? styles.textareaError : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <textarea
        id={id}
        className={textareaCls}
        rows={rows}
        aria-describedby={errorId ?? hintId}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}
